import { useEffect, useState } from "react";

/**
 * LogoMarquee — infinite-scrolling client logos. Used on the events overview
 * and the Claude Design Workshop landing. No header text, no surrounding
 * rules — just the logo strip.
 *
 * The marquee animation is paused until after window.load — running it from
 * frame 0 was causing Lighthouse's trace engine to lose track of the hero's
 * LCP candidate (continuous transform paints decouple from the main-thread
 * trace timeline).
 */

const CHARCOAL_MUTED = "hsla(60, 3%, 8%, 0.65)";
const CREAM = "hsl(48, 20%, 92%)";
const HAIRLINE = "hsla(60, 3%, 8%, 0.18)";

// `scale` renders an individual logo taller than the shared 32px base so a
// compact lockup (the Salesforce cloud+wordmark) matches the plain wordmarks.
// `w`/`h` are each SOURCE FILE's real pixel dimensions, not a render size. They
// exist so the width/height attributes carry the logo's true aspect ratio: the
// shared 120x32 was reserving a wrongly-shaped box for every logo while it was
// still lazy-loading (Lighthouse image-aspect-ratio, plus the shift when the
// real image landed). Rendered size stays with the CSS below — 32px tall in the
// rail, max 28px in the mobile grid.
type LogoItem = { base: string; alt: string; w: number; h: number; ext?: "webp" | "svg"; scale?: number };
const LOGOS: LogoItem[] = [
  { base: "/images/client-logos/forbes-2", alt: "Forbes", w: 375, h: 114 },
  { base: "/images/client-logos/nasdaq", alt: "Nasdaq", w: 600, h: 170 },
  { base: "/images/client-logos/espn", alt: "ESPN", w: 535, h: 160 },
  { base: "/images/client-logos/amc-networks", alt: "AMC Networks", w: 450, h: 155 },
  { base: "/images/client-logos/jetblue", alt: "JetBlue", w: 300, h: 102 },
  { base: "/images/client-logos/asurion", alt: "Asurion", w: 600, h: 222 },
  { base: "/images/client-logos/chase", alt: "Chase", w: 600, h: 111 },
  { base: "/images/client-logos/salesforce", alt: "Salesforce", w: 76, h: 54, ext: "svg", scale: 1.9 },
  { base: "/images/client-logos/kars4kids", alt: "Kars4Kids", w: 300, h: 85 },
];

// Mobile-only third row. These three logos do not have the full avif/webp/png
// variant set so they're rendered as plain <img> with the webp source.
// These don't have the full avif/webp/png variant set so they're rendered
// as a plain <img>. PBS uses the SVG (newer asset Isaac uploaded 2026-05-25).
const MOBILE_EXTRA_LOGOS: Array<LogoItem & { ext: "webp" | "svg" }> = [
  { base: "/images/client-logos/tmobile", alt: "T-Mobile", w: 400, h: 81, ext: "webp" },
  { base: "/images/client-logos/paypal", alt: "PayPal", w: 200, h: 51, ext: "webp" },
  { base: "/images/client-logos/pbs", alt: "PBS", w: 135, h: 56, ext: "svg" },
];

export function LogoMarquee() {
  // Duplicate the logos once so the keyframe can translate from 0 → -50%
  // for a seamless infinite loop.
  const sequence = [...LOGOS, ...LOGOS];

  // Start the animation only after window.load — see component docstring.
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (document.readyState === "complete") {
      setLoaded(true);
      return;
    }
    const onLoad = () => setLoaded(true);
    window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  return (
    <section
      aria-label="Trusted by"
      style={{
        background: CREAM,
        position: "relative",
      }}
      className={`design-logo-mq ${loaded ? "design-logo-mq-loaded" : ""}`}
    >
      <style>{`
        .design-logo-mq {
          padding: 56px 0 64px;
        }
        @media (min-width: 768px) {
          .design-logo-mq {
            padding: 64px 0 72px;
          }
        }
        .design-logo-mq-head {
          display: flex;
          align-items: center;
          gap: 18px;
          max-width: 1280px;
          margin: 0 auto 36px;
          padding: 0 28px;
        }
        .design-logo-mq-eye,
        .design-logo-mq-meta {
          font-family: var(--font-mono);
          font-size: 10px;
          font-weight: 500;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: ${CHARCOAL_MUTED};
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          gap: 10px;
        }
        .design-logo-mq-rule {
          flex: 1;
          height: 1px;
          background: ${HAIRLINE};
        }
        @media (max-width: 767px) {
          .design-logo-mq-meta {
            display: none;
          }
          .design-logo-mq-head {
            margin-bottom: 24px;
          }
        }
        .design-logo-mq-rail {
          overflow: hidden;
          -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
                  mask-image: linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%);
        }
        @media (max-width: 767px) {
          .design-logo-mq-rail { display: none; }
        }
        /* MOBILE — eyebrow + 3-col grid matching the cowork detail page. */
        .design-logo-mq-mobile-eyebrow { display: none; }
        .design-logo-mq-grid { display: none; }
        @media (max-width: 767px) {
          .design-logo-mq-mobile-eyebrow {
            display: block;
            font-family: var(--font-mono);
            font-size: 15px;
            font-weight: 500;
            line-height: 1.45;
            letter-spacing: 0;
            text-transform: uppercase;
            color: ${CHARCOAL_MUTED};
            text-align: center;
            margin: 0 20px 22px;
            text-wrap: balance;
          }
          .design-logo-mq-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            border-top: 1px solid ${HAIRLINE};
            border-left: 1px solid ${HAIRLINE};
            margin: 0 20px;
          }
          .design-logo-mq-cell {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px 12px;
            border-right: 1px solid ${HAIRLINE};
            border-bottom: 1px solid ${HAIRLINE};
            min-height: 76px;
          }
          .design-logo-mq-cell img {
            max-height: 28px;
            max-width: 100%;
            width: auto;
            object-fit: contain;
            opacity: 0.6;
            filter: grayscale(1);
            mix-blend-mode: multiply;
          }
        }
        .design-logo-mq-track {
          display: flex;
          align-items: center;
          gap: 0;
          width: max-content;
          animation: design-logo-mq-scroll 38s linear infinite;
          /* Pause the marquee until after first paint so the continuous
             keyframe doesn't decouple the hero's paint event from
             Lighthouse's main-thread trace (causing NO_LCP on /events). */
          animation-play-state: paused;
        }
        .design-logo-mq-loaded .design-logo-mq-track {
          animation-play-state: running;
        }
        @keyframes design-logo-mq-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .design-logo-mq-track {
            animation: none;
          }
        }
        .design-logo-mq-item {
          display: inline-flex;
          align-items: center;
          padding: 12px 56px;
          white-space: nowrap;
        }
        .design-logo-mq-item img {
          height: 32px;
          width: auto;
          display: block;
          opacity: 0.5;
          filter: grayscale(1);
          mix-blend-mode: multiply;
        }
        .design-logo-mq-dot {
          font-family: var(--font-display);
          font-size: 38px;
          line-height: 1;
          color: ${HAIRLINE};
          font-weight: 400;
          transform: translateY(-0.18em);
          flex-shrink: 0;
        }
      `}</style>

      <div className="design-logo-mq-rail">
        <div className="design-logo-mq-track">
          {sequence.map((logo, i) => (
            <span key={`${logo.alt}-${i}`} style={{ display: "inline-flex", alignItems: "center" }}>
              <span className="design-logo-mq-item">
                {logo.ext ? (
                  <img
                    src={`${logo.base}.${logo.ext}`}
                    alt={logo.alt}
                    loading="lazy"
                    decoding="async"
                    width={logo.w}
                    height={logo.h}
                    style={logo.scale ? { height: `${32 * logo.scale}px` } : undefined}
                  />
                ) : (
                  <picture>
                    <source type="image/avif" srcSet={`${logo.base}.avif`} />
                    <source type="image/webp" srcSet={`${logo.base}.webp`} />
                    <img
                      src={`${logo.base}.png`}
                      alt={logo.alt}
                      loading="lazy"
                      decoding="async"
                      width={logo.w}
                      height={logo.h}
                    />
                  </picture>
                )}
              </span>
              <span className="design-logo-mq-dot" aria-hidden="true">·</span>
            </span>
          ))}
        </div>
      </div>

      <p className="design-logo-mq-mobile-eyebrow">
        Some of the brands attending our workshops (there are thousands more)
      </p>

      <div className="design-logo-mq-grid">
        {[...LOGOS, ...MOBILE_EXTRA_LOGOS].map((logo) => (
          <div key={logo.alt} className="design-logo-mq-cell">
            {logo.ext ? (
              <img
                src={`${logo.base}.${logo.ext}`}
                alt={logo.alt}
                loading="lazy"
                decoding="async"
                width={logo.w}
                height={logo.h}
                style={logo.scale ? { maxHeight: `${28 * logo.scale}px` } : undefined}
              />
            ) : (
              <picture>
                <source type="image/avif" srcSet={`${logo.base}.avif`} />
                <source type="image/webp" srcSet={`${logo.base}.webp`} />
                <img
                  src={`${logo.base}.png`}
                  alt={logo.alt}
                  loading="lazy"
                  decoding="async"
                  width={logo.w}
                  height={logo.h}
                />
              </picture>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
